import React from 'react';
import { Activity, Brain, Clock, HeartHandshake, ShieldCheck, Sparkles } from 'lucide-react';
import SimpleAudioButton from './audio/SimpleAudioButton';

const BenefitCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}> = ({ icon, title, description, color, bgColor }) => {
  return (
    <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:translate-y-[-5px]">
      <div className={`${bgColor} w-14 h-14 rounded-full flex items-center justify-center mb-5`}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const Benefits: React.FC = () => {
  const benefits = [
    {
      icon: <HeartHandshake className="w-7 h-7 text-[#F4B6A1]" />,
      title: "Emotional Validation",
      description: "Experience the relief of having your emotions acknowledged and validated, creating a foundation for emotional well-being.",
      color: "text-[#F4B6A1]",
      bgColor: "bg-[#F4B6A1]/20"
    },
    {
      icon: <Sparkles className="w-7 h-7 text-[#C2A4D6]" />,
      title: "Stress Reduction",
      description: "Regular emotional processing with an empathetic listener has been shown to significantly reduce stress levels.",
      color: "text-[#C2A4D6]",
      bgColor: "bg-[#C2A4D6]/20"
    },
    {
      icon: <Brain className="w-7 h-7 text-[#B3D9E6]" />,
      title: "Improved Clarity",
      description: "Articulating your thoughts helps organize them, leading to greater mental clarity and better decision-making.",
      color: "text-[#B3D9E6]",
      bgColor: "bg-[#B3D9E6]/20"
    },
    {
      icon: <Activity className="w-7 h-7 text-[#A9D0A1]" />,
      title: "Emotional Resilience",
      description: "Build emotional resilience through regular reflection and supportive interactions that strengthen your coping skills.",
      color: "text-[#A9D0A1]",
      bgColor: "bg-[#A9D0A1]/20"
    },
    {
      icon: <ShieldCheck className="w-7 h-7 text-[#F4B6A1]" />,
      title: "Safe Expression",
      description: "Express yourself freely without fear of judgment, allowing for more authentic emotional processing.",
      color: "text-[#F4B6A1]",
      bgColor: "bg-[#F4B6A1]/20"
    },
    {
      icon: <Clock className="w-7 h-7 text-[#C2A4D6]" />,
      title: "Anytime Support",
      description: "Get support exactly when you need it most, not just during scheduled appointment hours.",
      color: "text-[#C2A4D6]",
      bgColor: "bg-[#C2A4D6]/20"
    }
  ];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-[#B3D9E6]/20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#B3D9E6] font-medium">Benefits</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            Experience the <span className="text-[#C2A4D6]">Transformation</span>
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/cora-transformation.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather']">
            Beyond just feeling heard, our users experience significant improvements in their 
            emotional well-being, mental clarity, and daily functioning.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <BenefitCard 
              key={index}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
              color={benefit.color}
              bgColor={benefit.bgColor}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <a 
            href="#pricing" 
            className="inline-block bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-3 px-8 rounded-full text-lg font-medium transition-all transform hover:scale-105 shadow-md"
          >
            Start Your Healing Journey
          </a>
        </div>
      </div>
    </section>
  );
};

export default Benefits;